import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Header } from '../components/Header';
import { UserDetailsModal } from '../components/UserDetailsModal';
import { SubscriptionManagement } from '../components/SubscriptionManagement';
import { DeleteUserConfirm } from '../components/DeleteUserConfirm';
import { AuditLogViewer } from '../components/AuditLogViewer';
import TestReminderEmail from '../components/TestReminderEmail';
import { Loader2, AlertCircle, Shield, User, Trash2, UserPlus, Search, RefreshCw, CheckSquare, Square, Users, Filter, CreditCard, XCircle, CheckCircle, Clock, Ban, Eye, BarChart3, Download, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { getUserStatistics } from '../lib/admin';
import { useAuth } from '../contexts/useAuth';
import { isAdminType } from '../lib/adminRoles';

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
type UserStatistics = Awaited<ReturnType<typeof getUserStatistics>>;

interface UserData {
  id: string;
  email: string;
  admin_type: string;
  business_name: string;
  contact_name: string;
  phone: string;
  address: string;
  subscription_status: string;
  subscription_id: string;
  customer_id: string;
  created_at: string;
  onboarding_step?: number;
  service_types?: JsonValue;
  selected_tier?: JsonValue;
  selected_addons?: JsonValue;
  selected_services?: JsonValue;
  automation_requirements?: string;
  selected_platforms?: JsonValue;
}

interface LeadData {
  id: string;
  business_name?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  status: string;
  current_step: number;
  progress_percent: number;
  last_autosaved_at?: string | null;
  completed_at?: string | null;
  next_reminder_at?: string | null;
  reminder_stage?: string | null;
  reminder_count: number;
  rr_sync_status: string;
  rr_sync_error?: string | null;
  source?: string | null;
  landing_path?: string | null;
  latest_path?: string | null;
  event_count?: number;
  last_event_at?: string | null;
  latest_research_status?: string | null;
  latest_research_provider?: string | null;
  advisor_summary?: {
    bottleneck?: string | null;
    city_state?: string | null;
    source_note?: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

type Tab = 'admins' | 'users' | 'leads';
type SubscriptionFilter = 'all' | 'active' | 'pending' | 'canceled' | 'none';

export default function AdminDashboard() {
  const { adminType } = useAuth();
  const isSuperAdmin = adminType === 'super_admin';
  const [users, setUsers] = useState<UserData[]>([]);
  const [leads, setLeads] = useState<LeadData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('users');
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionFilter>('all');
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUsersForBulk, setSelectedUsersForBulk] = useState<string[]>([]);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const toggleUserSelection = (userId: string) => {
    setSelectedUsersForBulk(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    const regularUsers = filteredUsers.filter(user =>
      user.admin_type === 'none' || !user.admin_type
    );

    if (selectedUsersForBulk.length === regularUsers.length) {
      setSelectedUsersForBulk([]);
    } else {
      setSelectedUsersForBulk(regularUsers.map(user => user.id));
    }
  };

  const loadLeads = useCallback(async (leadSearch = '') => {
    try {
      const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
        body: {
          action: 'list_leads',
          limit: 200,
          search: leadSearch || undefined,
        },
      });
      if (error) throw error;
      setLeads(data?.leads || []);
    } catch (error) {
      console.error('Error loading onboarding leads:', error);
    }
  }, []);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await getUserStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(data || []);
      setSelectedAdmins([]);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const usersRequest = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    const leadsRequest = supabase.functions.invoke('onboarding-lead-capture', {
      body: {
        action: 'list_leads',
        limit: 200,
      },
    });

    Promise.allSettled([
      usersRequest,
      leadsRequest,
      getUserStatistics(),
    ])
      .then(([usersResult, leadsResult, statisticsResult]) => {
        if (!mounted) return;

        if (usersResult.status === 'fulfilled') {
          const { data, error } = usersResult.value;
          if (error) {
            setError(error.message);
          } else {
            setUsers(data || []);
            setSelectedAdmins([]);
            setError(null);
          }
        } else {
          console.error('Error loading users:', usersResult.reason);
          setError(usersResult.reason instanceof Error ? usersResult.reason.message : 'Failed to load users');
        }

        if (leadsResult.status === 'fulfilled') {
          if (leadsResult.value.error) {
            console.error('Error loading onboarding leads:', leadsResult.value.error);
          } else {
            setLeads(leadsResult.value.data?.leads || []);
          }
        } else {
          console.error('Error loading onboarding leads:', leadsResult.reason);
        }

        if (statisticsResult.status === 'fulfilled') {
          setStatistics(statisticsResult.value);
        } else {
          console.error('Error loading statistics:', statisticsResult.reason);
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const exportToCSV = () => {
    const usersToExport = selectedUsersForBulk.length > 0
      ? filteredUsers.filter(u => selectedUsersForBulk.includes(u.id))
      : filteredUsers;

    if (usersToExport.length === 0) {
      toast.error('No users to export');
      return;
    }

    const headers = [
      'Email',
      'Business Name',
      'Contact Name',
      'Phone',
      'Address',
      'Subscription Status',
      'Subscription ID',
      'Customer ID',
      'Onboarding Step',
      'Service Types',
      'Selected Tier',
      'Selected Services',
      'Automation Requirements',
      'Selected Platforms',
      'Admin Type',
      'Created At'
    ];

    const csvRows = [
      headers.join(',')
    ];

    usersToExport.forEach(user => {
      const formatField = (field: unknown) => {
        if (field === null || field === undefined) return '';
        if (typeof field === 'object') return JSON.stringify(field).replace(/"/g, '""');
        const str = String(field).replace(/"/g, '""');
        return `"${str}"`;
      };

      const row = [
        formatField(user.email),
        formatField(user.business_name),
        formatField(user.contact_name),
        formatField(user.phone),
        formatField(user.address),
        formatField(user.subscription_status || 'none'),
        formatField(user.subscription_id),
        formatField(user.customer_id),
        formatField(user.onboarding_step),
        formatField(user.service_types),
        formatField(user.selected_tier),
        formatField(user.selected_services),
        formatField(user.automation_requirements),
        formatField(user.selected_platforms),
        formatField(user.admin_type),
        formatField(new Date(user.created_at).toLocaleString())
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    const timestamp = new Date().toISOString().split('T')[0];
    const exportType = selectedUsersForBulk.length > 0 ? 'selected' : activeTab;
    link.setAttribute('download', `scalesmall-${exportType}-users-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${usersToExport.length} user${usersToExport.length === 1 ? '' : 's'}`);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([loadUsers(), loadLeads(searchTerm), loadStatistics()]);
    setIsRefreshing(false);
  };

  const handleAddAdmin = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Check if current user is super admin by querying users table
      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('admin_type')
        .eq('id', session.user.id)
        .maybeSingle();

      if (currentUserError) throw currentUserError;

      if (currentUserData?.admin_type !== 'super_admin') {
        throw new Error('Only the super admin can add new admins');
      }

      if (!newAdminEmail.trim()) {
        throw new Error('Email is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newAdminEmail.trim())) {
        throw new Error('Please enter a valid email address');
      }

      const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-admin-privileges`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: newAdminEmail.trim(),
          adminType: 'sub_admin',
          createIfNotExists: true,
          request_id: crypto.randomUUID(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to grant admin privileges');
      }

      const result = await response.json();

      await loadUsers();
      setShowAddAdmin(false);
      setNewAdminEmail('');

      toast.success(result.invited ? 'Administrator invite sent successfully' : 'Administrator privileges granted successfully');
    } catch (err) {
      console.error('Error adding admin:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Check if current user is super admin by querying users table
      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('admin_type')
        .eq('id', session.user.id)
        .maybeSingle();

      if (currentUserError) throw currentUserError;

      if (currentUserData?.admin_type !== 'super_admin') {
        throw new Error('Only the super admin can remove admins');
      }

      const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-admin-privileges`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          targetUserId: userId,
          adminType: 'none',
          request_id: crypto.randomUUID(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove admin privileges');
      }

      await loadUsers();
      toast.success('Admin privileges removed successfully');
    } catch (err) {
      console.error('Error removing admin:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to remove admin');
    }
  };

  const handleBulkRemove = async () => {
    try {
      setIsRemoving(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Check if current user is super admin by querying users table
      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('admin_type')
        .eq('id', session.user.id)
        .maybeSingle();

      if (currentUserError) throw currentUserError;

      if (currentUserData?.admin_type !== 'super_admin') {
        throw new Error('Only the super admin can remove admins');
      }

      const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-admin-privileges`;

      const removePromises = selectedAdmins.map(userId =>
        fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            targetUserId: userId,
            adminType: 'none',
            request_id: crypto.randomUUID(),
          })
        })
      );

      const responses = await Promise.all(removePromises);
      const failedResponses = responses.filter(r => !r.ok);

      if (failedResponses.length > 0) {
        throw new Error(`Failed to remove ${failedResponses.length} admin(s)`);
      }

      await loadUsers();
      setSelectedAdmins([]);
      toast.success(`Successfully removed admin privileges from ${selectedAdmins.length} user${selectedAdmins.length === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('Error removing admins:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to remove admins');
    } finally {
      setIsRemoving(false);
    }
  };

  const toggleAdminSelection = (userId: string) => {
    setSelectedAdmins(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllAdmins = () => {
    const adminUsers = filteredUsers.filter(user => 
      isAdminType(user.admin_type) && user.admin_type !== 'super_admin'
    );
    
    if (selectedAdmins.length === adminUsers.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(adminUsers.map(user => user.id));
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      user.email.toLowerCase().includes(searchLower) ||
      user.business_name?.toLowerCase().includes(searchLower) ||
      user.contact_name?.toLowerCase().includes(searchLower);

    const matchesTab = activeTab === 'admins'
      ? isAdminType(user.admin_type)
      : activeTab === 'users' && (user.admin_type === 'none' || !user.admin_type);

    const matchesSubscription = subscriptionFilter === 'all' || 
      user.subscription_status === subscriptionFilter;

    return matchesSearch && matchesTab && matchesSubscription;
  });

  const adminCount = users.filter(user => 
    isAdminType(user.admin_type) && user.admin_type !== 'super_admin'
  ).length;

  const userCount = users.filter(user => 
    user.admin_type === 'none' || !user.admin_type
  ).length;

  const filteredLeads = leads.filter((lead) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lead.business_name?.toLowerCase().includes(searchLower) ||
      lead.contact_name?.toLowerCase().includes(searchLower) ||
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.phone?.toLowerCase().includes(searchLower) ||
      lead.address?.toLowerCase().includes(searchLower) ||
      lead.advisor_summary?.bottleneck?.toLowerCase().includes(searchLower) ||
      lead.advisor_summary?.city_state?.toLowerCase().includes(searchLower) ||
      lead.status.toLowerCase().includes(searchLower)
    );
  });

  const getSubscriptionStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'canceled':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'none':
        return <Ban className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSubscriptionStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'Pending';
      case 'canceled':
        return 'Canceled';
      case 'none':
        return 'No Subscription';
      default:
        return 'Unknown';
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-500/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/10';
      case 'canceled':
        return 'text-red-400 bg-red-500/10';
      case 'none':
        return 'text-gray-400 bg-gray-500/10';
      default:
        return 'text-gray-400 bg-gray-500/10';
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setIsSendingEmail(true);
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-email`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Admin session is required');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || 'Failed to send test email';
        const errorDetails = data.details ? ` (${JSON.stringify(data.details)})` : '';
        throw new Error(errorMessage + errorDetails);
      }

      toast.success('Test email sent successfully');
    } catch (err) {
      console.error('Error sending test email:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send test email', {
        duration: 6000,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-scale-dark">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 pt-28 pb-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleSendTestEmail}
              disabled={isSendingEmail}
              className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send test email notification"
            >
              {isSendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Test Email
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center px-4 py-2 bg-green-500/10 text-green-400 rounded-md hover:bg-green-500/20 transition-colors"
              title={selectedUsersForBulk.length > 0 ? `Export ${selectedUsersForBulk.length} selected user${selectedUsersForBulk.length === 1 ? '' : 's'}` : 'Export filtered users'}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={handleRefresh}
              className={`p-2 text-gray-400 hover:text-white transition-colors ${isRefreshing ? 'animate-spin' : ''}`}
              disabled={isRefreshing}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowAuditLog(true)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="View audit logs"
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => setShowAddAdmin(true)}
                className="flex items-center px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Admin
              </button>
            )}
          </div>
        </div>

        {/* Test Reminder Email */}
        <div className="mb-6">
          <TestReminderEmail />
        </div>

        {/* Statistics Dashboard */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-scale-navy p-4 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Users</span>
                <Users className="w-5 h-5 text-scale-blue" />
              </div>
              <p className="text-2xl font-bold text-white">{statistics.totalUsers}</p>
            </div>
            <div className="bg-scale-navy p-4 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Active Subscriptions</span>
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold text-white">{statistics.activeSubscriptions}</p>
            </div>
            <div className="bg-scale-navy p-4 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Pending</span>
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <p className="text-2xl font-bold text-white">{statistics.pendingSubscriptions}</p>
            </div>
            <div className="bg-scale-navy p-4 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Admins</span>
                <Shield className="w-5 h-5 text-scale-blue" />
              </div>
              <p className="text-2xl font-bold text-white">{statistics.totalAdmins}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-scale-navy rounded-lg p-1">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md transition-colors ${
              activeTab === 'users'
                ? 'bg-scale-blue text-scale-dark'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Users ({userCount})
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md transition-colors ${
              activeTab === 'admins'
                ? 'bg-scale-blue text-scale-dark'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Shield className="w-4 h-4 mr-2" />
            Admins ({adminCount})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md transition-colors ${
              activeTab === 'leads'
                ? 'bg-scale-blue text-scale-dark'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Leads ({leads.length})
          </button>
        </div>

        {!isLoading && !error && (users.length > 0 || activeTab === 'leads') && (
          <div className="mb-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search ${activeTab === 'admins' ? 'admins' : activeTab === 'leads' ? 'leads' : 'users'} by email, business name, or contact name...`}
                  className="w-full pl-10 pr-4 py-2 bg-scale-navy border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
                />
              </div>

              {/* Subscription Filter */}
              {activeTab === 'users' && (
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={subscriptionFilter}
                    onChange={(e) => setSubscriptionFilter(e.target.value as SubscriptionFilter)}
                    className="pl-10 pr-8 py-2 bg-scale-navy border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent appearance-none"
                  >
                    <option value="all">All Subscriptions</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="canceled">Canceled</option>
                    <option value="none">No Subscription</option>
                  </select>
                </div>
              )}
            </div>

            {activeTab === 'users' && userCount > 0 && (
              <div className="flex items-center justify-between bg-scale-navy p-4 rounded-lg border border-gray-700">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={toggleAllUsers}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {selectedUsersForBulk.length === filteredUsers.filter(u => u.admin_type === 'none' || !u.admin_type).length && selectedUsersForBulk.length > 0 ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-white">
                    {selectedUsersForBulk.length} of {filteredUsers.filter(u => u.admin_type === 'none' || !u.admin_type).length} users selected
                  </span>
                </div>
              </div>
            )}

            {activeTab === 'admins' && isSuperAdmin && adminCount > 0 && (
              <div className="flex items-center justify-between bg-scale-navy p-4 rounded-lg border border-gray-700">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={toggleAllAdmins}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {selectedAdmins.length === adminCount ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-white">
                    {selectedAdmins.length} of {adminCount} admins selected
                  </span>
                </div>
                {selectedAdmins.length > 0 && (
                  <button
                    onClick={handleBulkRemove}
                    disabled={isRemoving}
                    className="flex items-center px-4 py-2 bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-colors"
                  >
                    {isRemoving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Removing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Selected
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Main Content Area */}
        <div className="bg-scale-navy bg-opacity-50 backdrop-blur-lg rounded-xl shadow-2xl p-8 border border-gray-800">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-scale-blue animate-spin" />
            </div>
          ) : error ? (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : activeTab === 'leads' ? (
            filteredLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-scale-blue/10 rounded-full flex items-center justify-center mb-4">
                  <BarChart3 className="w-8 h-8 text-scale-blue" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">No Leads Found</h2>
                <p className="text-gray-400 max-w-md">
                  No website visitors or onboarding drafts match your search.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLeads.map((lead) => (
                  <div key={lead.id} className="bg-scale-darker p-6 rounded-lg border border-gray-700">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="w-5 h-5 text-scale-blue" />
                          <h3 className="text-lg font-semibold text-white">
                            {lead.business_name || lead.contact_name || lead.email || 'Anonymous visitor'}
                          </h3>
                          <span className="px-2 py-1 bg-scale-blue/10 text-scale-blue text-xs rounded-full">
                            {lead.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-300">
                          <p><span className="text-gray-500">Contact:</span> {lead.contact_name || 'Not entered'}</p>
                          <p><span className="text-gray-500">Email:</span> {lead.email || 'Not entered'}</p>
                          <p><span className="text-gray-500">Phone:</span> {lead.phone || 'Not entered'}</p>
                          <p><span className="text-gray-500">City / area:</span> {lead.address || lead.advisor_summary?.city_state || 'Not entered'}</p>
                          <p><span className="text-gray-500">Source:</span> {lead.source || 'unknown'}</p>
                          <p><span className="text-gray-500">Last page:</span> {lead.latest_path || lead.landing_path || 'unknown'}</p>
                          <p><span className="text-gray-500">Last saved:</span> {lead.last_autosaved_at ? new Date(lead.last_autosaved_at).toLocaleString() : 'Not saved yet'}</p>
                          <p><span className="text-gray-500">Website activity:</span> {Number(lead.event_count || 0)} event{Number(lead.event_count || 0) === 1 ? '' : 's'}{lead.last_event_at ? `, last ${new Date(lead.last_event_at).toLocaleString()}` : ''}</p>
                        </div>
                        {(lead.advisor_summary?.bottleneck || lead.latest_research_status || lead.advisor_summary?.source_note) && (
                          <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm text-gray-300">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-emerald-300">Advisor capture</span>
                              {lead.latest_research_status && (
                                <span className="rounded-full bg-gray-800 px-2 py-1 text-xs text-gray-300">
                                  Research: {lead.latest_research_status}{lead.latest_research_provider ? ` via ${lead.latest_research_provider}` : ''}
                                </span>
                              )}
                            </div>
                            {lead.advisor_summary?.bottleneck && (
                              <p><span className="text-gray-500">Main problem:</span> {lead.advisor_summary.bottleneck}</p>
                            )}
                            {lead.advisor_summary?.source_note && (
                              <p className="mt-1"><span className="text-gray-500">Public lookup:</span> {lead.advisor_summary.source_note}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="min-w-[240px] space-y-3">
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>Progress</span>
                            <span>{Math.round(Number(lead.progress_percent || 0))}%</span>
                          </div>
                          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-scale-blue"
                              style={{ width: `${Math.min(100, Math.max(0, Number(lead.progress_percent || 0)))}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 space-y-1">
                          <p>Step {lead.current_step + 1} of 7</p>
                          <p>Reminder: {lead.next_reminder_at ? `${lead.reminder_stage || 'scheduled'} at ${new Date(lead.next_reminder_at).toLocaleDateString()}` : 'not scheduled'}</p>
                          <p>R&R sync: <span className={lead.rr_sync_status === 'synced' ? 'text-green-400' : lead.rr_sync_status === 'failed' ? 'text-red-400' : 'text-yellow-400'}>{lead.rr_sync_status}</span></p>
                          {lead.rr_sync_error && <p className="text-red-400">R&R error: {lead.rr_sync_error}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-scale-blue/10 rounded-full flex items-center justify-center mb-4">
                {activeTab === 'admins' ? (
                  <Shield className="w-8 h-8 text-scale-blue" />
                ) : (
                  <Users className="w-8 h-8 text-scale-blue" />
                )}
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                No {activeTab === 'admins' ? 'Admins' : 'Users'} Found
              </h2>
              <p className="text-gray-400 max-w-md">
                {activeTab === 'admins'
                  ? 'No administrators found matching your search criteria.'
                  : 'No users found matching your search criteria.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-scale-darker p-6 rounded-lg border border-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      {activeTab === 'users' && (
                        <button
                          onClick={() => toggleUserSelection(user.id)}
                          className="text-gray-400 hover:text-white transition-colors mt-1"
                        >
                          {selectedUsersForBulk.includes(user.id) ? (
                            <CheckSquare className="w-5 h-5" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      {activeTab === 'admins' && isSuperAdmin && user.admin_type !== 'super_admin' && (
                        <button
                          onClick={() => toggleAdminSelection(user.id)}
                          className="text-gray-400 hover:text-white transition-colors mt-1"
                        >
                          {selectedAdmins.includes(user.id) ? (
                            <CheckSquare className="w-5 h-5" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      {user.admin_type === 'none' || !user.admin_type ? (
                        <User className="w-8 h-8 text-gray-400 flex-shrink-0" />
                      ) : (
                        <Shield className="w-8 h-8 text-scale-blue flex-shrink-0" />
                      )}
                      <div className="flex-grow">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-medium">{user.email}</span>
                          {user.admin_type !== 'none' && (
                            <span className="px-2 py-1 bg-scale-blue/10 text-scale-blue text-xs rounded-full">
                              {user.admin_type === 'super_admin' ? 'Super Admin' : 'Admin'}
                            </span>
                          )}
                        </div>

                        {/* Subscription Status */}
                        {activeTab === 'users' && (
                          <div className="flex items-center space-x-2 mb-2">
                            {getSubscriptionStatusIcon(user.subscription_status || 'none')}
                            <span className={`px-2 py-1 text-xs rounded-full ${getSubscriptionStatusColor(user.subscription_status || 'none')}`}>
                              {getSubscriptionStatusText(user.subscription_status || 'none')}
                            </span>
                            {user.subscription_id && (
                              <span className="text-xs text-gray-400">
                                ID: {user.subscription_id.substring(0, 8)}...
                              </span>
                            )}
                          </div>
                        )}
                        
                        {user.business_name && (
                          <div className="text-gray-300 mb-1">
                            <span className="font-medium">Business:</span> {user.business_name}
                          </div>
                        )}
                        
                        {user.contact_name && (
                          <div className="text-gray-300 mb-1">
                            <span className="font-medium">Contact:</span> {user.contact_name}
                          </div>
                        )}
                        
                        {user.phone && (
                          <div className="text-gray-300 mb-1">
                            <span className="font-medium">Phone:</span> {user.phone}
                          </div>
                        )}
                        
                        {user.address && (
                          <div className="text-gray-300 mb-1">
                            <span className="font-medium">Address:</span> {user.address}
                          </div>
                        )}

                        <div className="text-gray-400 text-sm mt-2">
                          <span className="font-medium">Joined:</span> {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {activeTab === 'admins' && isSuperAdmin && user.admin_type !== 'super_admin' && (
                      <button
                        onClick={() => handleRemoveAdmin(user.id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors"
                        title="Remove admin privileges"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}

                    {activeTab === 'users' && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetails(true);
                          }}
                          className="p-2 text-scale-blue hover:text-white transition-colors"
                          title="View details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {user.subscription_id && (
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowSubscriptionManagement(true);
                            }}
                            className="p-2 text-green-400 hover:text-green-300 transition-colors"
                            title="Manage subscription"
                          >
                            <CreditCard className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                          title="Delete user"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showAddAdmin && isSuperAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-scale-navy rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700" role="dialog" aria-modal="true" aria-labelledby="admin-invite-title">
              <h3 id="admin-invite-title" className="text-xl font-semibold text-white mb-4">Invite Administrator</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="admin-invite-email" className="block text-sm font-medium text-white mb-1">
                    Email Address
                  </label>
                  <input
                    id="admin-invite-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                    placeholder="Enter email address"
                  />
                  <p className="mt-2 text-sm text-gray-400">
                    New administrators receive a secure email invitation and choose their own password. Existing users are granted administrator privileges without changing their password.
                  </p>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowAddAdmin(false)}
                    className="px-4 py-2 text-white bg-gray-700 rounded hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddAdmin}
                    className="px-4 py-2 bg-scale-blue text-scale-dark rounded hover:bg-white transition-colors"
                  >
                    Send Admin Invite
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUserDetails && selectedUser && (
          <UserDetailsModal
            userId={selectedUser.id}
            onClose={() => {
              setShowUserDetails(false);
              setSelectedUser(null);
            }}
            onUserUpdated={() => {
              loadUsers();
              loadStatistics();
            }}
          />
        )}

        {showSubscriptionManagement && selectedUser && (
          <SubscriptionManagement
            userEmail={selectedUser.email}
            subscriptionId={selectedUser.subscription_id}
            customerId={selectedUser.customer_id}
            subscriptionStatus={selectedUser.subscription_status}
            onClose={() => {
              setShowSubscriptionManagement(false);
              setSelectedUser(null);
            }}
          />
        )}

        {showDeleteConfirm && selectedUser && (
          <DeleteUserConfirm
            userId={selectedUser.id}
            userEmail={selectedUser.email}
            userName={selectedUser.business_name || selectedUser.contact_name}
            onClose={() => {
              setShowDeleteConfirm(false);
              setSelectedUser(null);
            }}
            onUserDeleted={() => {
              loadUsers();
              loadStatistics();
            }}
          />
        )}

        {showAuditLog && (
          <AuditLogViewer
            onClose={() => setShowAuditLog(false)}
          />
        )}
      </div>
    </div>
  );
}
